/**
 * PegasusBoardBlock (SCHED-02 + BOARD-PLACE + BOARD-SLIDE) — a Pegasus-scheduled
 * job in a tech's board lane. Drag to another tech's lane to reassign, or between
 * two calls to sequence it (the drop sets the internal time only — the promised
 * window never auto-changes; the card OFFERS a window change via a confirm chip).
 * While dragging, a live drop indicator shows where it will land (the whole
 * column is the drop target, so a drop anywhere on a tech registers — Seth
 * 2026-06-24). Click opens the detail; right-click opens a quick-action menu.
 */
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Phone, Sparkles, Check, X, History } from "lucide-react";
import { cn } from "@/lib/cn";
import { windowColorClasses, usePlusAgreements } from "./api";
import {
  useMoveDispatch,
  useUpdateTicket,
  useLogDispatchEvent,
  useRescheduleLogger,
  windowForTime,
  type PendingTicket,
} from "./queue";
import { PegasusJobPeek } from "./PegasusJobPeek";
import { BoardCardMenu } from "./BoardCardMenu";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { useFieldStatusMap, chicagoTodayISO, FieldChip } from "@/features/mobile/field";

type DropTarget = { empno: string | null; beforeKey: string | null; time: string | undefined };

export function PegasusBoardBlock({
  job,
  density = "cards",
  onSelect,
  onDragPreview,
  windowPrompt,
  onWindowPrompt,
  boardDate,
}: {
  job: PendingTicket;
  density?: "compact" | "cards";
  onSelect?: (job: PendingTicket) => void;
  /** Live drop preview (which lane + before which item) while dragging this card. */
  onDragPreview?: (p: { empno: string; beforeKey: string | null } | null) => void;
  /** Board-level "set window?" prompt (keyed by job id; survives a cross-tech move). */
  windowPrompt?: { jobId: string; window: string } | null;
  onWindowPrompt?: (p: { jobId: string; window: string } | null) => void;
  /** The day the board is showing — a job dated EARLIER is a carryover (CARRYOVER). */
  boardDate?: string;
}) {
  const [peek, setPeek] = useState(false);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [reschedule, setReschedule] = useState(false);
  const urgent = job.urgency === "asap";
  const cm = job.call_ahead_method === "text" ? "Text" : "Call";
  // Board-level prompt, read by id so it survives a cross-tech remount.
  const pendingWindow = windowPrompt?.jobId === job.id ? windowPrompt.window : null;
  const c = windowColorClasses(job.scheduled_window);
  const subtitle = `${job.service_type_name ?? job.summary}${job.city ? ` · ${job.city}` : ""}`;
  // BOARD-HD v2 (Seth 2026-09-01: no promised time on cards; names never cut
  // off): street address + gold PLUS chip on the card — parity with the
  // ESC-side cards. The window pill says when.
  const plusSet = usePlusAgreements().data;
  const isPlus = !!(job.custno && plusSet?.has(job.custno));
  const addr = [job.address, job.city].filter(Boolean).join(" · ");
  // CARRYOVER: a still-scheduled call whose day is EARLIER than the board's day
  // rode forward because it was never completed — flag it amber so the office
  // follows up (it's NOT today's fresh work).
  const carried = !!(
    boardDate &&
    job.scheduled_date &&
    job.scheduled_date < boardDate
  );
  // Completed calls stay on their day, marked DONE (Seth 2026-08-11: a
  // completed Pegasus dispatch vanished from the board entirely).
  const done = job.status === "completed";
  // FIELD-M1a: the tech's live state from Pegasus Field (OTW / On site / Wrapped).
  const fieldRow = useFieldStatusMap(chicagoTodayISO()).data?.get(job.id);
  const carriedLabel =
    carried && job.scheduled_date
      ? (() => {
          const d = new Date(`${job.scheduled_date}T12:00:00`);
          return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${d.getMonth() + 1}/${d.getDate()}`;
        })()
      : "";
  const carriedTitle = carried
    ? `Carried over from ${carriedLabel} — not completed yet. Follow up.`
    : undefined;
  const move = useMoveDispatch();
  const update = useUpdateTicket();
  const log = useLogDispatchEvent();
  const logReschedule = useRescheduleLogger();
  const wasDragged = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  const lastPreview = useRef<string>("");
  const rescheduleBefore = useRef<{ date: string | null; window: string | null; empno: string | null } | null>(null);

  // Where would a drop at (x,y) land? The whole column carries data-drop-empno;
  // the items inside carry data-mins/data-key. Hides self for the hit-test so we
  // see the lane underneath the lifted card.
  const computeDropTarget = (x: number, y: number): DropTarget | null => {
    const self = cardRef.current;
    const prevPE = self?.style.pointerEvents;
    if (self) self.style.pointerEvents = "none";
    const col = document.elementFromPoint(x, y)?.closest("[data-drop-empno]");
    if (self) self.style.pointerEvents = prevPE ?? "";
    if (!col) return null;
    const empno = col.getAttribute("data-drop-empno");
    const wrappers = [...col.querySelectorAll("[data-mins]")]
      .filter((el) => el.getAttribute("data-key") !== `p-${job.id}`)
      .map((el) => {
        const r = el.getBoundingClientRect();
        return { key: el.getAttribute("data-key"), mins: Number(el.getAttribute("data-mins")), mid: r.top + r.height / 2 };
      })
      .sort((a, b) => a.mid - b.mid);
    let idx = wrappers.findIndex((w) => y < w.mid);
    if (idx === -1) idx = wrappers.length;
    const prev = wrappers[idx - 1];
    const next = wrappers[idx];
    const pm = prev && prev.mins >= 0 ? prev.mins : null;
    const nm = next && next.mins >= 0 ? next.mins : null;
    let mins: number | null;
    if (pm != null && nm != null) mins = Math.round((pm + nm) / 2);
    else if (pm != null) mins = pm + 30;
    else if (nm != null) mins = nm - 30;
    else mins = null;
    let time: string | undefined;
    if (mins == null) time = undefined;
    else {
      mins = Math.max(7 * 60, Math.min(20 * 60, mins));
      time = `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}:00`;
    }
    return { empno, beforeKey: next ? next.key : null, time };
  };

  const onDrag = (e: MouseEvent | TouchEvent | PointerEvent) => {
    const p = e as PointerEvent;
    const t = computeDropTarget(p.clientX, p.clientY);
    const key = t && t.empno ? `${t.empno}|${t.beforeKey}` : "";
    if (key === lastPreview.current) return;
    lastPreview.current = key;
    onDragPreview?.(t && t.empno ? { empno: t.empno, beforeKey: t.beforeKey } : null);
  };

  const onDrop = (e: MouseEvent | TouchEvent | PointerEvent) => {
    const p = e as PointerEvent;
    const t = computeDropTarget(p.clientX, p.clientY);
    if (!t) return;
    const changingTech = !!t.empno && t.empno !== job.assigned_empno;
    if (!changingTech && t.time === undefined) return;
    const v: { id: string; empno?: string | null; time?: string | null } = { id: job.id };
    if (changingTech) v.empno = t.empno;
    if (t.time !== undefined) v.time = t.time;
    move.mutate(v);
    // OFFER a window change if the drop lands in a different band than the
    // current promise — works same-lane AND cross-tech (the prompt is board-level
    // so it survives the remount). Never auto-apply (12-4 in front of 12-4 stays).
    if (t.time) {
      const implied = windowForTime(t.time);
      if (implied && implied !== job.scheduled_window) {
        onWindowPrompt?.({ jobId: job.id, window: implied });
      }
    }
  };
  const onDragEnd = (e: MouseEvent | TouchEvent | PointerEvent) => {
    onDrop(e);
    onDragPreview?.(null);
    lastPreview.current = "";
    window.setTimeout(() => { wasDragged.current = false; }, 0);
  };
  // Rich hover card — same delayed portal preview as the ESC DispatchBlock, with
  // the full job notes (Seth 2026-06-24: match the ESC hover + show my notes).
  const startHover = (e: React.MouseEvent) => {
    const x = e.clientX, y = e.clientY;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHoverPos({ x, y }), 350);
  };
  const endHover = () => { if (hoverTimer.current) window.clearTimeout(hoverTimer.current); setHoverPos(null); };
  const openDetail = () => { endHover(); if (onSelect) onSelect(job); else setPeek(true); };
  const openPeek = () => {
    if (wasDragged.current) { wasDragged.current = false; return; }
    openDetail();
  };
  // Open the dispatch in the MODAL WINDOW (not the cockpit bottom strip) — Seth:
  // double-click / right-click "Open dispatch" should pop the same window the
  // Columns view does, in cockpit + map too. The board owns the modal; a window
  // event avoids threading a callback through 6 layers (BoardView listens).
  const openWindow = () => {
    endHover();
    window.dispatchEvent(new CustomEvent("pegasus:open-dispatch", { detail: job }));
  };
  const onContext = (e: React.MouseEvent) => { e.preventDefault(); endHover(); setMenu({ x: e.clientX, y: e.clientY }); };

  const confirmWindow = () => {
    if (!pendingWindow) return;
    update.mutate({ id: job.id, scheduled_window: pendingWindow });
    log.mutate({ dispatch_id: job.id, kind: "window_changed", summary: `Window ${job.scheduled_window ?? "Any"} → ${pendingWindow} (confirmed after drag)` });
    onWindowPrompt?.(null);
  };

  const openReschedule = () => {
    rescheduleBefore.current = { date: job.scheduled_date, window: job.scheduled_window, empno: job.assigned_empno };
    setReschedule(true);
  };
  const closeReschedule = async () => {
    setReschedule(false);
    if (rescheduleBefore.current) await logReschedule(job.id, rescheduleBefore.current);
  };

  const dragHandlers = {
    drag: true as const,
    dragSnapToOrigin: true,
    dragMomentum: false,
    whileDrag: { scale: 1.03, opacity: 0.85, zIndex: 50, boxShadow: "0 12px 28px rgba(15,23,42,0.25)" },
    onDragStart: () => { wasDragged.current = true; endHover(); },
    onDrag,
    onDragEnd,
    onClick: openPeek,
    // Double-click → the modal WINDOW (parity with the ESC card opening the full
    // record; Pegasus has no full-page route so it pops the modal). Single-click
    // still does the cockpit strip / columns-modal as before (Seth 2026-06-25).
    onDoubleClick: openWindow,
    onContextMenu: onContext,
    onMouseEnter: startHover,
    onMouseLeave: endHover,
    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Enter") openDetail(); },
  };

  const compactBlock = (
    <motion.div
      ref={cardRef}
      role="button"
      tabIndex={0}
      {...dragHandlers}
      title={carriedTitle}
      className={cn(
        "w-full cursor-grab rounded border-l-[3px] px-1.5 py-1 text-left leading-tight transition-[filter] hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        done ? "bg-emerald-50/80 text-emerald-900 border-l-emerald-400" : carried ? "bg-amber-50 text-amber-900 border-l-amber-400" : cn(c.bg, c.text, c.border),
      )}
    >
      <div className="flex items-baseline gap-1">
        {done ? <Check className="h-3 w-3 shrink-0 self-center text-emerald-600" /> : carried ? <History className="h-3 w-3 shrink-0 self-center text-amber-600" /> : <Sparkles className="h-3 w-3 shrink-0 self-center opacity-70" />}
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{job.customer_name}</span>
        {job.is_test && <span title="Test job — never messages anyone" className="shrink-0 rounded bg-rose-600 px-1 text-[9px] font-bold text-white">TEST</span>}
        {done && <span className="shrink-0 rounded bg-emerald-200 px-1 text-[9px] font-bold text-emerald-900">DONE</span>}
        {!done && <FieldChip row={fieldRow} dense />}
        {carried && <span className="shrink-0 rounded bg-amber-200 px-1 text-[9px] font-bold text-amber-900">{carriedLabel}</span>}
        {job.scheduled_window && <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide opacity-80">{job.scheduled_window}</span>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="min-w-0 flex-1 truncate text-[10px] opacity-85">{subtitle}</span>
        {isPlus && (
          <span
            title="PLUS member — active maintenance plan"
            className="shrink-0 rounded-sm bg-white/80 px-1 text-[9px] font-extrabold tracking-wide text-amber-700"
          >
            PLUS
          </span>
        )}
        {job.call_ahead && <Phone className="h-2.5 w-2.5 shrink-0 self-center opacity-70" />}
        {urgent && <span className="shrink-0 rounded bg-red-600 px-1 text-[9px] font-bold text-white">ASAP</span>}
      </div>
    </motion.div>
  );

  const cardBlock = (
    <motion.div
      ref={cardRef}
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      {...dragHandlers}
      title={carriedTitle}
      className={cn(
        "group relative block w-full cursor-grab overflow-hidden rounded-lg border border-l-4 border-black/[0.08] bg-white px-3 py-2.5 text-left shadow-sm transition-all",
        "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        done ? "border-l-emerald-400 ring-1 ring-emerald-200" : carried ? "border-l-amber-400 ring-1 ring-amber-200" : c.border,
      )}
    >
      <span aria-hidden className={cn("pointer-events-none absolute inset-0", done ? "bg-emerald-400" : carried ? "bg-amber-400" : c.bg, "opacity-[0.22]")} />
      <span className="relative block">
        <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1.5">
          {done && (
            <span className="inline-flex items-center gap-1 rounded bg-emerald-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-900">
              <Check className="h-3 w-3" /> Done
            </span>
          )}
          {carried && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900">
              <History className="h-3 w-3" /> from {carriedLabel}
            </span>
          )}
          {job.scheduled_window && (
            <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", c.bg, c.text)}>{job.scheduled_window}</span>
          )}
          <span className="inline-flex items-center gap-1 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-700">
            <Sparkles className="h-3 w-3" /> Pegasus
          </span>
          {isPlus && (
            <span
              title="PLUS member — active maintenance plan"
              className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ring-1 ring-amber-300/70"
            >
              Plus
            </span>
          )}
          {!done && <FieldChip row={fieldRow} />}
          {urgent && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">ASAP</span>}
          {job.is_test && <span title="Test job — never messages anyone" className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Test</span>}
        </div>
        {/* Name wraps — never cut off (BOARD-HD v2). */}
        <div className="text-[13.5px] font-bold leading-snug text-slate-900">{job.customer_name}</div>
        <div className="text-xs leading-snug text-slate-500">{job.service_type_name ?? job.summary}</div>
        {addr && <div className="mt-0.5 text-[11.5px] leading-snug text-slate-500">{addr}</div>}
        {job.call_ahead && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500">
            <Phone className="h-3 w-3" /> {cm} on the way{job.call_ahead_phone ? ` · ${job.call_ahead_name ? `${job.call_ahead_name} ` : ""}${job.call_ahead_phone}` : ""}
          </div>
        )}
      </span>
    </motion.div>
  );

  return (
    <>
      <div className="relative">
        {density === "compact" ? compactBlock : cardBlock}
        {pendingWindow && (
          <div
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute -top-3 left-2 z-40 flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 shadow"
          >
            Set window to {pendingWindow}?
            <button type="button" aria-label={`set window to ${pendingWindow}`} onClick={confirmWindow} className="rounded-full bg-amber-200 p-1 text-amber-900 hover:bg-amber-300"><Check className="h-3.5 w-3.5" /></button>
            <button type="button" aria-label="keep current window" onClick={() => onWindowPrompt?.(null)} className="rounded-full p-1 hover:bg-amber-100"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      {hoverPos && createPortal(
        <div
          className="pointer-events-none fixed z-[60] w-80"
          style={{ left: Math.min(hoverPos.x + 16, window.innerWidth - 336), top: Math.max(8, Math.min(hoverPos.y - 40, window.innerHeight - 300)) }}
        >
          <div className="rounded-xl border border-hairline bg-white p-3 shadow-e3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-brand-500" aria-hidden />
              <span className="text-sm font-semibold text-slate-900">{job.customer_name}</span>
              {job.scheduled_window && (
                <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", c.bg, c.text)}>{job.scheduled_window}</span>
              )}
              {urgent && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">ASAP</span>}
              {job.is_test && <span className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Test job — never messages anyone</span>}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">{job.service_type_name ?? job.summary}</div>
            {(job.address || job.city) && (
              <div className="text-[11px] text-slate-500">{[job.address, job.city].filter(Boolean).join(", ")}</div>
            )}
            {job.call_ahead && (
              <div className="text-[11px] text-slate-500">{cm} on the way{job.call_ahead_phone ? ` · ${job.call_ahead_name ? `${job.call_ahead_name} ` : ""}${job.call_ahead_phone}` : ""}</div>
            )}
            {job.notes && (
              <div className="mt-1.5 max-h-48 overflow-hidden whitespace-pre-wrap border-t border-hairline pt-1.5 text-[11px] leading-snug text-slate-600">{job.notes}</div>
            )}
          </div>
        </div>,
        document.body,
      )}
      {peek && createPortal(<PegasusJobPeek job={job} onClose={() => setPeek(false)} />, document.body)}
      {menu && (
        <BoardCardMenu
          job={job}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          onOpenDetail={openWindow}
          onReschedule={openReschedule}
        />
      )}
      {reschedule && (
        <CreateDispatchModal
          existingTicket={job}
          custno={job.custno}
          locno={job.locno}
          customerName={job.customer_name}
          address={job.address}
          city={job.city}
          phone={job.phone}
          onClose={closeReschedule}
        />
      )}
    </>
  );
}
